/**
 * Super-admin subscription management — add, renew, remove, and alert expiring accounts.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import swal from 'sweetalert';
import Sidebar_Progress from '../../../layouts/Admin_Sidebar_Progress.component';
import LoginHeaderOperational from '../../LoginHeaderOperational';
import { API_URL } from '../../../../constant/API_Settings';
import { fetchPlatformSuperAdminProfile } from '../../../../utils/platformAuth';
import {
  adminAddSubscription,
  adminDeleteSubscription,
  adminMarkSubscriptionPaid,
  adminNotifyPump,
  adminRenewSubscription,
  fetchAdminPlans,
  fetchAdminSubscriptions,
  fetchPumpNames,
} from '../adminApi';
import AdminSubscriptionBillingModal from './AdminSubscriptionBillingModal';
import AdminSubscriptionControlModal from './AdminSubscriptionControlModal';
import {
  BILLING_MONTH_OPTIONS,
  addMonthsToDateInput,
  formatCurrency,
  quotePlanPurchase,
} from '../../Manager/SubscriptionManagement/subscriptionApi';
import '../admin-dashboard.css';
import './AdminSubscriptionManagement.css';

const FILTERS = [
  { id: '', label: 'All pumps' },
  { id: 'active', label: 'Active' },
  { id: 'expiring', label: 'Expiring soon' },
  { id: 'expired', label: 'Expired' },
  { id: 'unpaid', label: 'Unpaid' },
  { id: 'none', label: 'No subscription' },
];

const PER_PAGE_OPTIONS = [20, 50, 100, 200];

function todayDateString() {
  const date = new Date();
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

function daysTone(row) {
  if (row.is_expired) return 'danger';
  if (row.is_expiring_soon) return 'warn';
  return 'ok';
}

function statusClass(row) {
  if (!row.has_subscription) return 'none';
  if (row.is_expired) return 'expired';
  if (row.is_expiring_soon) return 'expiring';
  return 'active';
}

function emptyAddForm(overrides = {}) {
  return {
    pump_id: '',
    plan_id: '',
    start_date: todayDateString(),
    end_date: '',
    billing_months: 1,
    note: '',
    use_custom_pricing: false,
    custom_amount: '',
    discount_amount: '',
    auto_approve_invoice: true,
    record_payment: false,
    payment_amount: '',
    payment_mode: 'cash',
    payment_reference: '',
    paid_by: '',
    ...overrides,
  };
}

function expiryAlertMessage(row) {
  const endDate = row.subscription_end_date;
  if (row.is_expired) {
    return `Petrosmart subscription notice\n\nYour subscription for ${row.pump_name} expired on ${endDate}. Please renew to continue uninterrupted access.\n\nContact support if you need assistance.`;
  }
  return `Petrosmart subscription reminder\n\nYour subscription for ${row.pump_name} ends on ${endDate} (${row.days_remaining} day(s) remaining). Please renew before expiry to avoid service interruption.`;
}

const AdminSubscriptionManagement = (props) => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [expiringSoon, setExpiringSoon] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0, per_page: 100 });
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [perPage, setPerPage] = useState(100);
  const [quickPumpId, setQuickPumpId] = useState('');
  const [plans, setPlans] = useState([]);
  const [pumps, setPumps] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [billingRow, setBillingRow] = useState(null);
  const [controlPumpId, setControlPumpId] = useState(null);
  const [addForm, setAddForm] = useState(() => emptyAddForm());
  const skipSearchDebounce = useRef(true);

  const selectedPlan = useMemo(
    () => plans.find((p) => String(p.id) === String(addForm.plan_id)),
    [plans, addForm.plan_id]
  );

  const purchaseQuote = useMemo(
    () => (selectedPlan ? quotePlanPurchase(selectedPlan, addForm.billing_months) : null),
    [selectedPlan, addForm.billing_months]
  );

  const computedEndDate = useMemo(
    () => addMonthsToDateInput(addForm.start_date, addForm.billing_months),
    [addForm.start_date, addForm.billing_months]
  );

  const load = useCallback((page = 1, query = search, activeFilter = filter, pageSize = perPage) => {
    setLoading(true);
    fetchAdminSubscriptions({ search: query, filter: activeFilter, page, perPage: pageSize })
      .then((res) => {
        setRows(res.data || []);
        setStats(res.stats || {});
        setExpiringSoon(res.expiring_soon || []);
        setPagination(res.pagination || {});
      })
      .catch((err) => swal('Error', err.message, 'error'))
      .finally(() => setLoading(false));
  }, [search, filter, perPage]);

  useEffect(() => {
    fetchPlatformSuperAdminProfile(API_URL, localStorage.getItem('AUTH_TOKEN'))
      .then((profile) => {
        if (!profile) {
          history.goBack();
          return;
        }
        Promise.all([fetchAdminPlans(), fetchPumpNames()])
          .then(([planRes, pumpRes]) => {
            setPlans(planRes.plans || []);
            setPumps(pumpRes.pump_detail || []);
          })
          .catch(() => {});
        load(1, '', '');
      })
      .catch(() => history.goBack());
  }, [history, load]);

  useEffect(() => {
    if (skipSearchDebounce.current) {
      skipSearchDebounce.current = false;
      return undefined;
    }
    const timer = setTimeout(() => load(1, search, filter), 350);
    return () => clearTimeout(timer);
  }, [search, filter, load]);

  function handlePerPageChange(nextPerPage) {
    setPerPage(nextPerPage);
    load(1, search, filter, nextPerPage);
  }

  function openAddForPump(pumpId) {
    setAddForm(emptyAddForm({ pump_id: String(pumpId) }));
    setShowAddModal(true);
  }

  function setBillingMonths(months, extra = {}) {
    const nextMonths = Math.min(36, Math.max(1, parseInt(months, 10) || 1));
    const quote = selectedPlan ? quotePlanPurchase(selectedPlan, nextMonths) : null;
    setAddForm({
      ...addForm,
      ...extra,
      billing_months: nextMonths,
      payment_amount: addForm.record_payment && quote
        ? String(quote.payable_amount)
        : addForm.payment_amount,
    });
  }

  function buildBillingPayload(form) {
    const quote = selectedPlan ? quotePlanPurchase(selectedPlan, form.billing_months) : null;
    const payload = {
      plan_id: form.plan_id,
      start_date: form.start_date,
      note: form.note || undefined,
      auto_approve_invoice: form.auto_approve_invoice,
      billing_months: Math.min(36, Math.max(1, parseInt(form.billing_months, 10) || 1)),
    };
    if (form.end_date) {
      payload.end_date = form.end_date;
    }
    if (form.use_custom_pricing) {
      payload.custom_amount = Number(form.custom_amount || 0);
      payload.discount_amount = Number(form.discount_amount || 0);
    } else if (form.discount_amount !== '' && form.discount_amount != null) {
      payload.discount_amount = Number(form.discount_amount || 0);
    }
    if (form.record_payment) {
      const customPayable = form.use_custom_pricing
        ? Number(form.custom_amount || 0)
        : null;
      payload.payment_amount = Number(
        form.payment_amount
          || customPayable
          || quote?.payable_amount
          || 0
      );
      payload.payment_mode = form.payment_mode;
      payload.payment_reference = form.payment_reference || undefined;
      payload.paid_by = form.paid_by || undefined;
    }
    return payload;
  }

  function handleQuickPumpSelect(pumpId) {
    setQuickPumpId(pumpId);
    if (!pumpId) {
      setSearch('');
      load(1, '', filter, perPage);
      return;
    }
    const pump = pumps.find((p) => String(p.id) === String(pumpId));
    if (!pump) return;
    setSearch(pump.pump_name);
    load(1, pump.pump_name, filter, perPage);
  }

  function handleFilterChange(nextFilter) {
    setFilter(nextFilter);
    load(1, search, nextFilter, perPage);
  }

  function handlePage(page) {
    load(page, search, filter, perPage);
  }

  function handleAddSubscription() {
    if (!addForm.pump_id) {
      swal('Pump required', 'Select a pump', 'warning');
      return;
    }
    if (!addForm.plan_id) {
      swal('Plan required', 'Select a subscription plan', 'warning');
      return;
    }
    adminAddSubscription(addForm.pump_id, buildBillingPayload(addForm))
      .then((res) => {
        swal('Success', res.message || 'Subscription added', 'success');
        setShowAddModal(false);
        setAddForm(emptyAddForm());
        load(pagination.current_page || 1, search, filter, perPage);
      })
      .catch((err) => swal('Error', err.message, 'error'));
  }

  function handleRenew(row) {
    swal({
      title: `Renew ${row.pump_name}?`,
      text: `Adds a new subscription period after ${row.subscription_end_date}. You can choose a different plan from the Add subscription dialog.`,
      icon: 'info',
      buttons: {
        cancel: 'Cancel',
        add: { text: 'Choose plan', value: 'add' },
        same: { text: 'Same plan', value: 'same' },
      },
    }).then((choice) => {
      if (!choice) return;
      if (choice === 'add') {
        openAddForPump(row.pump_id);
        return;
      }
      adminRenewSubscription(row.id, { auto_approve_invoice: true })
        .then((res) => {
          swal('Success', res.message || 'Subscription renewed', 'success');
          load(pagination.current_page || 1, search, filter, perPage);
        })
        .catch((err) => swal('Error', err.message, 'error'));
    });
  }

  function handleMarkPaid(row) {
    adminMarkSubscriptionPaid(row.id)
      .then((res) => {
        swal('Success', res.message || 'Payment recorded', 'success');
        load(pagination.current_page || 1, search, filter, perPage);
      })
      .catch((err) => swal('Error', err.message, 'error'));
  }

  function handleDelete(row) {
    swal({
      title: 'Remove subscription?',
      text: `Remove subscription record for ${row.pump_name}? This cannot be undone.`,
      icon: 'warning',
      buttons: true,
      dangerMode: true,
    }).then((ok) => {
      if (!ok) return;
      adminDeleteSubscription(row.id)
        .then((res) => {
          swal('Removed', res.message, 'success');
          load(pagination.current_page || 1, search, filter, perPage);
        })
        .catch((err) => swal('Error', err.message, 'error'));
    });
  }

  function handleNotify(row) {
    const message = expiryAlertMessage(row);
    swal({
      title: `Alert ${row.pump_name}?`,
      text: message,
      icon: 'info',
      buttons: ['Cancel', 'Send email'],
    }).then((ok) => {
      if (!ok) return;
      adminNotifyPump(row.pump_id, message, ['email'], 'subscription_expiry')
        .then((res) => swal('Sent', res.message || 'Notification sent', 'success'))
        .catch((err) => swal('Error', err.message, 'error'));
    });
  }

  const kpiCards = [
    { label: 'All pumps', value: stats.total || 0, tone: 'default' },
    { label: 'With subscription', value: stats.with_subscription || 0, tone: 'default' },
    { label: 'Active', value: stats.active || 0, tone: 'success' },
    { label: 'Expiring (30d)', value: stats.expiring_soon || 0, tone: 'warning' },
    { label: 'Expired', value: stats.expired || 0, tone: 'danger' },
    { label: 'No subscription', value: stats.without_subscription || 0, tone: 'warning' },
  ];

  return (
    <div className="main-container page-wrap admin-platform admin-subscription-page">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={21} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title="Subscription Management" />
          <div className="container pb-3">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3 mb-2">
              <div>
                <Link to="/admin_dashboard" className="small">← Control Center</Link>
                <p className="text-muted small mb-0 mt-1">
                  View every pump&apos;s current subscription. Open Manage for full control —
                  change plan, custom pricing, invoices, receipts, discounts, and audit trail.
                </p>
              </div>
              <button type="button" className="btn btn-primary" onClick={() => setShowAddModal(true)}>
                + Add subscription
              </button>
            </div>

            <div className="admin-sub-kpi-grid">
              {kpiCards.map((card) => (
                <div key={card.label} className={`admin-sub-kpi admin-sub-kpi--${card.tone}`}>
                  <p className="admin-sub-kpi__value">{card.value}</p>
                  <p className="admin-sub-kpi__label">{card.label}</p>
                </div>
              ))}
            </div>

            {expiringSoon.length > 0 && (
              <div className="admin-sub-expiring-panel">
                <h6>Accounts ending soon — alert customers</h6>
                {expiringSoon.map((row) => (
                  <div key={`exp-${row.pump_id}-${row.id}`} className="admin-sub-expiring-item">
                    <div>
                      <div className="admin-sub-expiring-item__name">{row.pump_name}</div>
                      <div className="admin-sub-expiring-item__meta">
                        Ends {row.subscription_end_date}
                        {row.is_expired ? ' (expired)' : ` · ${row.days_remaining} day(s) left`}
                        {row.pump_email ? ` · ${row.pump_email}` : ''}
                        {row.pump_phone ? ` · ${row.pump_phone}` : ''}
                      </div>
                    </div>
                    <div className="admin-action-row">
                      <button type="button" className="btn btn-sm btn-outline-warning" onClick={() => handleNotify(row)}>
                        Alert
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => handleRenew(row)}>
                        Renew
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="admin-panel">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <div>
                  <h5 className="mb-0">Pump subscriptions</h5>
                  <p className="small text-muted mb-0">
                    {pagination.total ? `${pagination.total} pump(s) total` : 'All pumps on the platform'}
                  </p>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <select
                    className="form-select form-select-sm admin-sub-quick-pump"
                    value={quickPumpId}
                    onChange={(e) => handleQuickPumpSelect(e.target.value)}
                    aria-label="Quick select pump"
                  >
                    <option value="">Quick select pump…</option>
                    {pumps.map((pump) => (
                      <option key={pump.id} value={pump.id}>{pump.pump_name}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    style={{ maxWidth: 280 }}
                    placeholder="Search pump, email, phone, plan..."
                    value={search}
                    onChange={(e) => {
                      setQuickPumpId('');
                      setSearch(e.target.value);
                    }}
                  />
                </div>
              </div>

              <div className="admin-sub-toolbar">
                <div className="admin-sub-filter-pills">
                  {FILTERS.map((item) => (
                    <button
                      key={item.id || 'all'}
                      type="button"
                      className={`admin-sub-filter-pill ${filter === item.id ? 'active' : ''}`}
                      onClick={() => handleFilterChange(item.id)}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
                <label className="admin-sub-per-page">
                  <span className="small text-muted">Show</span>
                  <select
                    className="form-select form-select-sm"
                    value={perPage}
                    onChange={(e) => handlePerPageChange(Number(e.target.value))}
                  >
                    {PER_PAGE_OPTIONS.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </label>
              </div>

              {loading ? (
                <p className="text-muted py-4 text-center">Loading subscriptions...</p>
              ) : rows.length === 0 ? (
                <p className="text-muted py-4 text-center">No pumps match your filters.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover admin-pump-table">
                    <thead>
                      <tr>
                        <th>Pump</th>
                        <th>Plan</th>
                        <th>Period</th>
                        <th>Days left</th>
                        <th>Payment</th>
                        <th>Invoice</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.id ? `sub-${row.id}` : `pump-${row.pump_id}`}>
                          <td>
                            <strong>{row.pump_name}</strong>
                            <div className="small text-muted">
                              {[row.pump_email, row.pump_phone].filter(Boolean).join(' · ') || '—'}
                            </div>
                            <Link to={`/admin-pump/${row.pump_id}`} className="small">Manage pump</Link>
                          </td>
                          <td>
                            {row.has_subscription ? (
                              <>
                                <div>{row.plan_name}</div>
                                <div className="small text-muted">₹{Number(row.amount).toLocaleString('en-IN')}</div>
                              </>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>
                            {row.has_subscription ? (
                              <>
                                <div className="small">{row.subscription_start_date}</div>
                                <div className="small text-muted">to {row.subscription_end_date}</div>
                              </>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>
                            {row.has_subscription ? (
                              <span className={`admin-sub-days admin-sub-days--${daysTone(row)}`}>
                                {row.is_expired ? `${Math.abs(row.days_remaining)}d ago` : `${row.days_remaining}d`}
                              </span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>{row.has_subscription ? (row.is_paid ? 'Paid' : 'Pending') : '—'}</td>
                          <td>
                            {row.billing ? (
                              <div className="small">
                                <div>#{row.billing.invoice_id} · {row.billing.workflow}</div>
                                {row.billing.amount_pending > 0 && (
                                  <div className="text-warning">Due ₹{Number(row.billing.amount_pending).toLocaleString('en-IN')}</div>
                                )}
                              </div>
                            ) : row.has_subscription ? (
                              <span className="text-muted small">No invoice</span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>
                            <span className={`admin-sub-status admin-sub-status--${statusClass(row)}`}>
                              {row.status_label}
                            </span>
                          </td>
                          <td>
                            <div className="admin-action-row">
                              <button
                                type="button"
                                className="btn btn-sm btn-dark"
                                onClick={() => setControlPumpId(row.pump_id)}
                              >
                                Manage
                              </button>
                              {row.has_subscription ? (
                                <>
                                  <button type="button" className="btn btn-sm btn-primary" onClick={() => openAddForPump(row.pump_id)}>
                                    Add plan
                                  </button>
                                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setBillingRow(row)}>
                                    Invoice / pay
                                  </button>
                                  {(row.is_expiring_soon || row.is_expired) && (
                                    <button type="button" className="btn btn-sm btn-outline-warning" onClick={() => handleNotify(row)}>
                                      Alert
                                    </button>
                                  )}
                                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => handleRenew(row)}>
                                    Renew
                                  </button>
                                  {!row.is_paid && (
                                    <button type="button" className="btn btn-sm btn-outline-success" onClick={() => handleMarkPaid(row)}>
                                      Mark paid
                                    </button>
                                  )}
                                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row)}>
                                    Remove
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={() => openAddForPump(row.pump_id)}
                                >
                                  Add subscription
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {pagination.last_page > 1 && (
                <div className="d-flex justify-content-center gap-1 mt-2 flex-wrap">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={pagination.current_page <= 1}
                    onClick={() => handlePage(pagination.current_page - 1)}
                  >
                    Previous
                  </button>
                  <span className="align-self-center small text-muted px-2">
                    Page {pagination.current_page} of {pagination.last_page}
                    {pagination.total ? ` · ${pagination.total} pumps` : ''}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={pagination.current_page >= pagination.last_page}
                    onClick={() => handlePage(pagination.current_page + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showAddModal && (
        <>
          <div className="admin-sub-modal-backdrop" onClick={() => setShowAddModal(false)} />
          <div className="admin-sub-modal" role="dialog" aria-modal="true">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Add subscription</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowAddModal(false)} />
            </div>
            <div className="mb-3">
              <label className="form-label small">Pump</label>
              <select
                className="form-select"
                value={addForm.pump_id}
                onChange={(e) => setAddForm({ ...addForm, pump_id: e.target.value })}
              >
                <option value="">Select pump</option>
                {pumps.map((pump) => (
                  <option key={pump.id} value={pump.id}>{pump.pump_name}</option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label small">Plan</label>
              <select
                className="form-select"
                value={addForm.plan_id}
                onChange={(e) => {
                  const planId = e.target.value;
                  const plan = plans.find((p) => String(p.id) === String(planId));
                  const quote = plan ? quotePlanPurchase(plan, addForm.billing_months) : null;
                  setAddForm({
                    ...addForm,
                    plan_id: planId,
                    payment_amount: addForm.record_payment && quote ? String(quote.payable_amount) : addForm.payment_amount,
                  });
                }}
              >
                <option value="">Select plan</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.plan_name} — {formatCurrency(plan.amount)} / {plan.duration} days
                    {plan.max_nozzles ? ` · ≤${plan.max_nozzles} nozzles` : ''}
                    {plan.max_tanks ? ` · ≤${plan.max_tanks} tanks` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label small">Billing period</label>
              <div className="admin-sub-month-pills">
                {BILLING_MONTH_OPTIONS.map((months) => {
                  const offer = (selectedPlan?.advance_offers || []).find((row) => Number(row.months) === months);
                  return (
                    <button
                      key={months}
                      type="button"
                      className={`admin-sub-month-pill ${Number(addForm.billing_months) === months ? 'is-selected' : ''}`}
                      onClick={() => setBillingMonths(months)}
                    >
                      {months} {months === 1 ? 'month' : 'months'}
                      {offer ? <span>{offer.discount_percent}% off</span> : null}
                    </button>
                  );
                })}
              </div>
              <div className="d-flex align-items-center gap-2 mt-2">
                <label className="small text-muted mb-0" htmlFor="admin-billing-months">Custom months</label>
                <input
                  id="admin-billing-months"
                  type="number"
                  min="1"
                  max="36"
                  className="form-control form-control-sm"
                  style={{ width: 90 }}
                  value={addForm.billing_months}
                  onChange={(e) => setBillingMonths(e.target.value)}
                />
              </div>
              {purchaseQuote && (
                <div className="admin-sub-quote mt-2">
                  <div className="d-flex justify-content-between">
                    <span>Monthly rate</span>
                    <strong>{formatCurrency(purchaseQuote.monthly_amount)}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>{purchaseQuote.months} month{purchaseQuote.months === 1 ? '' : 's'}</span>
                    <strong>{formatCurrency(purchaseQuote.gross_amount)}</strong>
                  </div>
                  {purchaseQuote.discount_amount > 0 && (
                    <div className="d-flex justify-content-between text-success">
                      <span>Advance discount ({purchaseQuote.discount_percent}%)</span>
                      <strong>−{formatCurrency(purchaseQuote.discount_amount)}</strong>
                    </div>
                  )}
                  <div className="d-flex justify-content-between">
                    <span>GST ({purchaseQuote.gst_percent || 0}%)</span>
                    <strong>+{formatCurrency(purchaseQuote.gst_amount)}</strong>
                  </div>
                  <div className="d-flex justify-content-between mt-1">
                    <span>Payable (incl. GST)</span>
                    <strong>{formatCurrency(purchaseQuote.payable_amount)}</strong>
                  </div>
                </div>
              )}
            </div>
            <div className="row g-2 mb-3">
              <div className="col-6">
                <label className="form-label small">Start date</label>
                <input
                  type="date"
                  className="form-control"
                  value={addForm.start_date}
                  onChange={(e) => setAddForm({ ...addForm, start_date: e.target.value })}
                />
              </div>
              <div className="col-6">
                <label className="form-label small">End date</label>
                <input
                  type="date"
                  className="form-control"
                  value={addForm.end_date || computedEndDate}
                  onChange={(e) => setAddForm({ ...addForm, end_date: e.target.value })}
                />
                <div className="form-text">Defaults from months; override for custom periods.</div>
              </div>
            </div>
            <div className="mb-3 border rounded p-2">
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="admin-custom-pricing"
                  checked={Boolean(addForm.use_custom_pricing)}
                  onChange={(e) => setAddForm({
                    ...addForm,
                    use_custom_pricing: e.target.checked,
                    custom_amount: e.target.checked && purchaseQuote
                      ? String(purchaseQuote.payable_amount)
                      : addForm.custom_amount,
                  })}
                />
                <label className="form-check-label" htmlFor="admin-custom-pricing">
                  Custom amount / discount (override plan quote)
                </label>
              </div>
              {addForm.use_custom_pricing && (
                <div className="row g-2">
                  <div className="col-6">
                    <label className="form-label small">Payable amount (₹)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control"
                      value={addForm.custom_amount}
                      onChange={(e) => setAddForm({ ...addForm, custom_amount: e.target.value })}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small">Discount shown (₹)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control"
                      value={addForm.discount_amount}
                      onChange={(e) => setAddForm({ ...addForm, discount_amount: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label small">Note (optional)</label>
              <input
                type="text"
                className="form-control"
                value={addForm.note}
                onChange={(e) => setAddForm({ ...addForm, note: e.target.value })}
                placeholder="Internal note"
              />
            </div>
            <div className="border rounded p-3 mb-3 bg-light">
              <h6 className="mb-2">Invoice & payment</h6>
              <label className="d-flex align-items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={addForm.auto_approve_invoice}
                  onChange={(e) => setAddForm({ ...addForm, auto_approve_invoice: e.target.checked })}
                />
                <span className="small">Approve invoice immediately (client can download)</span>
              </label>
              <label className="d-flex align-items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={addForm.record_payment}
                  onChange={(e) => setAddForm({
                    ...addForm,
                    record_payment: e.target.checked,
                    payment_amount: e.target.checked && purchaseQuote ? String(purchaseQuote.payable_amount) : addForm.payment_amount,
                  })}
                />
                <span className="small">Record payment now</span>
              </label>
              {addForm.record_payment && (
                <div className="row g-2 mt-1">
                  <div className="col-6">
                    <label className="form-label small">Amount (₹)</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={addForm.payment_amount}
                      onChange={(e) => setAddForm({ ...addForm, payment_amount: e.target.value })}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small">Mode</label>
                    <select
                      className="form-select form-select-sm"
                      value={addForm.payment_mode}
                      onChange={(e) => setAddForm({ ...addForm, payment_mode: e.target.value })}
                    >
                      <option value="cash">Cash</option>
                      <option value="bank_transfer">Bank transfer</option>
                      <option value="cheque">Cheque</option>
                      <option value="upi">UPI</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleAddSubscription}>
                Save subscription
              </button>
            </div>
          </div>
        </>
      )}

      {billingRow && (
        <AdminSubscriptionBillingModal
          row={billingRow}
          onClose={() => setBillingRow(null)}
          onSaved={() => load(pagination.current_page || 1, search, filter, perPage)}
        />
      )}

      {controlPumpId && (
        <AdminSubscriptionControlModal
          pumpId={controlPumpId}
          plans={plans}
          onClose={() => setControlPumpId(null)}
          onSaved={() => load(pagination.current_page || 1, search, filter, perPage)}
        />
      )}
    </div>
  );
};

export default AdminSubscriptionManagement;
